import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  careCommunicationReadEvent,
  isInboundUnread,
  loadReadIds,
  markMessageRead,
  notifyCareCommunicationRead,
  type InboundLetter,
} from '@/lib/medcom/careCommunicationRead';
import { clinicReadStore } from '@/lib/medcom/careCommunicationReadStore';
import {
  publishReadKey,
  pullClinicReads,
  siblingReadKeys,
} from '@/lib/medcom/careCommunicationReadSync';

const READ_POLL_MS = 10_000;

export function useCareCommunicationReads(
  clinicId: string | null | undefined,
  letters?: readonly InboundLetter[],
) {
  const [version, setVersion] = useState(0);
  const [marked, setMarked] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    const event = careCommunicationReadEvent();
    window.addEventListener(event, bump);
    return () => window.removeEventListener(event, bump);
  }, []);

  useEffect(() => {
    if (!clinicId) return;
    let cancelled = false;
    const pull = () => {
      void pullClinicReads(clinicId, localStorage, clinicReadStore).then((merged) => {
        if (cancelled || !merged) return;
        setMarked((current) => {
          let changed = false;
          const next = new Set(current);
          for (const id of merged) {
            if (!id || next.has(id)) continue;
            next.add(id);
            changed = true;
          }
          return changed ? next : current;
        });
      });
    };
    pull();
    const onVisible = () => {
      if (document.visibilityState === 'visible') pull();
    };
    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') pull();
    }, READ_POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [clinicId]);

  const readIds = useMemo(() => {
    const ids = clinicId ? loadReadIds(localStorage, clinicId) : new Set<string>();
    for (const id of marked) ids.add(id);
    return ids;
  }, [clinicId, version, marked]);

  const isUnread = useCallback(
    (letter: InboundLetter) => isInboundUnread(letter, readIds),
    [readIds],
  );

  const markRead = useCallback(
    (messageId: string) => {
      if (!messageId) return;
      setMarked((current) => {
        if (current.has(messageId)) return current;
        const next = new Set(current);
        next.add(messageId);
        return next;
      });
      if (!clinicId) return;
      if (markMessageRead(localStorage, clinicId, messageId)) notifyCareCommunicationRead();
      void publishReadKey(clinicId, messageId, clinicReadStore);
    },
    [clinicId],
  );

  useEffect(() => {
    if (!letters || letters.length === 0) return;
    for (const key of siblingReadKeys(letters, readIds)) markRead(key);
  }, [letters, readIds, markRead]);

  return { isUnread, markRead };
}
